/**
 * Add or edit a template.
 *
 * A template is wording you have already got right, kept so it is written once
 * rather than every time. That only works if it can hold what campaigns are
 * actually made of — this had a plain textarea, so an email built out of
 * tables and buttons could be pasted in but appeared as raw markup, could not
 * be previewed, and had no way to carry a picture. A library you cannot read
 * is not a library.
 *
 * So it is the campaign editor, the same one, with the same preview beside it.
 * Anything that can be sent can now be kept.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { CHANNELS, channelLabel } from '../../constants/marketing.js';
import Sheet, { Group, Field } from '../Sheet.jsx';
import CampaignEditor, { looksHtml } from './CampaignEditor.jsx';
import MailPreview from '../MailPreview.jsx';
import { useInlineImages } from '../../lib/inlineImages.js';

export default function TemplateSheet({ id, preset, onClose }) {
  const { S, mkt, saveTemplate, toast } = useApp();

  const existing = id ? mkt.templates.find(t => t.id === id) : null;

  /* Arriving from the Email screen means an email template, and an existing
     one is already whatever it is. */
  const fixedChannel = !!preset || !!id;

  const [form, setForm] = useState(() => existing || {
    name: '', channel: preset || 'email', subject: '', body: ''
  });
  const [html, setHtml] = useState(() => looksHtml((existing && existing.body) || ''));

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const isEmail = form.channel === 'email';

  /*
   * Shown with the merge fields still in it. A template has no recipient, so
   * there is nothing to fill them from — and a preview that quietly emptied
   * them would hide the one mistake worth catching here, which is a field
   * spelled wrong.
   */
  const shown = useInlineImages(String(form.body || ''));

  const save = () => {
    if (!String(form.name).trim()) { toast('Name the template'); return; }
    saveTemplate(id, { ...form, name: String(form.name).trim() });
    onClose();
  };

  return (
    <Sheet
      icon="file"
      tone="g-orange"
      title={id ? 'Edit template' : 'New template'}
      wide
      onClose={onClose}
      onSave={save}
      saveLabel="Save template"
    >
      <Group>Template</Group>

      <Field label="Name" hint={<div className="fhint">What you will look for it under.</div>}>
        <input
          autoFocus
          value={form.name || ''}
          placeholder="Trial ending — convert"
          onChange={e => set('name', e.target.value)}
        />
      </Field>

      <Field label="Channel">
        {/*
          * Fixed when it is already known — started from a channel's own
          * screen, or editing one that exists. Wording written for email has a
          * subject line and markup; the same words as an SMS are 160
          * characters of plain text. Switching underneath is not an edit.
          */}
        <select
          value={form.channel}
          disabled={fixedChannel}
          onChange={e => set('channel', e.target.value)}
        >
          {(fixedChannel ? CHANNELS.filter(([v]) => v === form.channel) : CHANNELS)
            .map(([v, n]) => <option key={v} value={v}>{n}</option>)}
        </select>
      </Field>

      {isEmail ? (
        <Field
          label="Subject"
          wide
          hint={
            <div className="fhint">
              Carried into the campaign along with the message. Merge fields work here too.
            </div>
          }
        >
          <input
            value={form.subject || ''}
            placeholder="Your trial ends on Friday"
            onChange={e => set('subject', e.target.value)}
          />
        </Field>
      ) : null}

      <Field label="Message" wide>
        <CampaignEditor
          channel={form.channel}
          html={html}
          onHtml={setHtml}
          body={form.body}
          onBody={v => set('body', v)}
          signature={S.signature || S.senderName || ''}
        />
      </Field>

      <Field label="Preview" wide>
        {String(form.body || '').trim() ? (
          <div className="cpreview">
            {isEmail && form.subject ? <div className="cpsub">{form.subject}</div> : null}
            {isEmail && looksHtml(form.body)
              ? <MailPreview html={shown} />
              : <div className="cpbody">{form.body}</div>}
          </div>
        ) : (
          <div className="cpreview">
            <div className="cpbody">Nothing written yet.</div>
          </div>
        )}
        <div className="fhint">
          Merge fields are left as they are — a template has nobody to fill them from, and a
          preview that emptied them would hide the one mistake worth catching here: a field
          spelled wrong. {channelLabel(form.channel)} campaigns can start from this.
        </div>
      </Field>
    </Sheet>
  );
}
